import React, { useEffect, useMemo, useRef, useState } from 'react';
import { updateMeeting, reasonOf, keyOf, PLATFORMS } from '../../lib/meetings';
import { chatPeople } from '../../lib/sessionLink';
import { Avatar, isContractor, ItTag } from '../todos/TodoPeople';
import { Icon } from '../todos/icons';

/*
 * THE DETAILS OF A MEETING NOBODY PLANNED, filled in while it records
 * (Gordon, 26 October): the title, who is here - NCMB staff and contractors
 * from the chat directory, and anybody else BY NAME, because the person on
 * the other end of the phone has no account and nobody wants to type an
 * email at a meeting table - and, for a call, which app it was on.
 *
 * Saved with one PATCH /meetings/:id carrying the whole list, as the edit
 * form does. The server sends nobody an invitation for an ad-hoc meeting
 * (lib/meetings.js invite), so adding Rita here records that she was there;
 * it does not email her about a meeting she is sitting in. The recording
 * carries on regardless: nothing here touches the recorder.
 *
 * Mounted by RoomRecorder on an ad-hoc meeting, before, during and after
 * the recording; the same fields are on the meeting page's Edit afterwards.
 */

const fromMeeting = (m) => ({
  title: m?.title || '',
  platform: m?.platform || '',
  attendees: (m?.attendees || []).filter((a) => a.role !== 'organiser' && a.key !== m?.organiser_key).map((a) => (a.kind === 'guest'
    ? (a.email ? { kind: 'guest', email: a.email, name: a.name || '' } : { kind: 'guest', name: a.name || '' })
    : { kind: a.kind === 'contractor' ? 'contractor' : 'user', key: a.key, name: a.name }))
});

const idOf = (a) => a.key || (a.email ? `g:${String(a.email).toLowerCase()}` : `n:${String(a.name || '').trim().toLowerCase()}`);

export default function AdhocDetails({ meeting, onSaved, live = false }) {
  const [f, setF] = useState(() => fromMeeting(meeting));
  const [people, setPeople] = useState(null);
  const [q, setQ] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const nameRef = useRef(null);
  const set = (patch) => { setF((cur) => ({ ...cur, ...patch })); setDirty(true); setSaved(false); };

  // The meeting read again (a reload, a save elsewhere) resets the form only
  // while nothing here is half-typed.
  useEffect(() => { if (!dirty) setF(fromMeeting(meeting)); }, [meeting, dirty]);

  useEffect(() => {
    let on = true;
    chatPeople().then((rows) => { if (on) setPeople(rows); }).catch(() => { if (on) setPeople([]); });
    return () => { on = false; };
  }, []);

  const chosen = useMemo(() => new Set(f.attendees.map(idOf)), [f.attendees]);
  const hits = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return (people || []).map((p) => ({ ...p, key: keyOf(p) }))
      .filter((p) => p.key && p.name && !chosen.has(p.key) && String(p.name).toLowerCase().includes(t)).slice(0, 6);
  }, [q, people, chosen]);

  const addPerson = (p) => { set({ attendees: [...f.attendees, { kind: isContractor(p) ? 'contractor' : 'user', key: p.key, name: p.name }] }); setQ(''); };
  const addName = () => {
    const n = name.trim().slice(0, 120);
    if (!n) return;
    if (!chosen.has(`n:${n.toLowerCase()}`)) set({ attendees: [...f.attendees, { kind: 'guest', name: n }] });
    setName('');
    nameRef.current?.focus();
  };
  const remove = (a) => set({ attendees: f.attendees.filter((x) => idOf(x) !== idOf(a)) });

  const save = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const title = f.title.trim();
    if (!title) { setError('Give the meeting a title.'); return; }
    setBusy(true);
    setError(null);
    try {
      const body = {
        title,
        attendees: f.attendees.map((a) => (a.kind === 'guest' ? (a.email ? { kind: 'guest', email: a.email, ...(a.name ? { name: a.name } : {}) } : { kind: 'guest', name: a.name }) : { kind: a.kind, key: a.key })),
        ...(meeting?.kind === 'call' ? { platform: f.platform || null } : {})
      };
      const m = await updateMeeting(meeting.id, body, 'this');
      setDirty(false);
      setSaved(true);
      if (onSaved) onSaved(m);
    } catch (err) {
      setError(reasonOf(err, 'Could not save the details.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={`rr-details${live ? ' is-live' : ''}`} onSubmit={save} noValidate data-adhoc-details="">
      {error && <p className="rr-trouble" role="alert">{error}</p>}
      <label className="tds-field rr-details-title">
        <span>Title</span>
        <input type="text" value={f.title} maxLength={200} aria-label="Meeting title" placeholder="What this meeting is about"
          onChange={(e) => set({ title: e.target.value })} />
      </label>
      {meeting?.kind === 'call' && (
        <label className="tds-field rr-details-platform">
          <span>Which app</span>
          <select value={f.platform} aria-label="Which app the call is on" onChange={(e) => set({ platform: e.target.value })}>
            <option value="">Not said</option>
            {PLATFORMS.map(([k, w]) => <option key={k} value={k}>{w}</option>)}
          </select>
        </label>
      )}
      <div className="rr-details-who">
        <span className="rr-details-label">Who is {live ? 'here' : 'here'}</span>
        <ul className="rr-names" aria-label="Attendees">
          {f.attendees.map((a) => (
            <li key={idOf(a)} className={`rr-name${a.kind === 'guest' ? ' is-guest' : ''}`} data-attendee={idOf(a)}>
              {a.key && <Avatar name={a.name} who={a.key} />}
              <span className="rr-name-text">{a.name || a.email}</span>
              {a.kind === 'contractor' && <ItTag />}
              <button type="button" className="rr-name-x" aria-label={`Remove ${a.name || a.email}`} onClick={() => remove(a)}>
                <Icon name="close" size={14} />
              </button>
            </li>
          ))}
          {f.attendees.length === 0 && <li className="rr-names-empty">Only you so far - add who is here, by name if they are not in Jarvis.</li>}
        </ul>
        <div className="rr-details-add">
          <div className="rr-pick">
            <input type="search" value={q} aria-label="Search NCMB staff and contractors" placeholder="NCMB staff or a contractor…"
              autoComplete="off" onChange={(e) => setQ(e.target.value)} />
            {q.trim() && (
              people === null ? <p className="rr-hint">Loading…</p>
                : hits.length === 0 ? <p className="rr-hint">Nobody by that name - add them by name below.</p>
                  : (
                    <ul className="rr-pick-list">
                      {hits.map((p) => (
                        <li key={p.key}>
                          <button type="button" className="rr-pick-item" data-person={p.key} onClick={() => addPerson(p)}>
                            <Avatar name={p.name} who={p.key} />
                            <span>{p.name}</span>
                            {isContractor(p) && <ItTag />}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )
            )}
          </div>
          <div className="rr-add">
            <input ref={nameRef} value={name} onChange={(e) => setName(e.target.value)} placeholder="Anybody else, by name"
              aria-label="Add a name" autoComplete="off" enterKeyHint="done"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addName(); } }} />
            <button type="button" className="btn btn-outline tds-btn rr-add-name" disabled={!name.trim()} onClick={addName}>
              <Icon name="plus" size={14} /> Add
            </button>
          </div>
        </div>
      </div>
      <div className="rr-details-foot">
        <button type="submit" className="btn btn-primary tds-btn rr-details-save" disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save details'}
        </button>
        <span className="rr-hint rr-details-note" role="status" aria-live="polite">
          {saved && !dirty ? <><Icon name="check" size={13} /> Saved. Nobody is emailed - the list says who was here.</>
            : live ? 'The recording carries on while you type.' : 'Can be filled in now, while recording, or afterwards.'}
        </span>
      </div>
    </form>
  );
}
